import { FormikErrors } from "formik";
import { ReactElement } from "react";

export interface SamlSettings {
    loginUrl: string;
}

export interface QuestionnaireSettings {
    uid: string;
    displayName: string;
    logoUri: string;
    baseUri: string;
    embed: boolean;
}

export interface Questionnaire {    
    uid: string;
    displayName: string;
    respondent: Respondent;
    template: Template;
    json: any;
    answers: any;
    accessCode: string;
    link: string;
    shortlivedAccessUri: string;
    persistentAccessUri: string;
    persistentAccessCode: string;
    percentageComplete: number;
    preloadedAnswersCount: number;
    notificationEmail: string;
    locked: boolean;
    createdAt: string;
    expiresAt: string;
    updatedAt: string;
}

export interface Respondent {
    uid: string;
    advisorUid: string;
    respondentName: string;
    respondentEmail: string;
}

export interface Template {
    uid?: string;
    key?: string;
    version?: string;
    displayName?: string;
    json?: any;
    advisor?: string;
    initialValues?: any;
    insertedAt?: string;
    active?: boolean;
}

export interface Upload {
    uid: string;
    displayName: string;
    questionnaireUid: string;
    data: string;
    fileFormat: string;
    evidenceType: string;
    extractedInfo: string;
    updatedAt: string;
}

export interface Case {
    uid: string;
    label: string;
    typeName: string;
    parent: NodeReference;
    created: string;
    name: string;
    notes: string;
    status: string;
    lastRun: string;
    targetDate: string;
    owner: NodeReference;
    organisationUid: string;
    numberOpenEnquiries: number;
    numberClosedEnquiries: number;
    numberReportsRequested: number;
    persistentAccessUri: string;
    completionNotificationUri: string;
    tags: any;
    childrenObjects: any[];
}

export interface FormFieldOutput {
    name: string;
    value: any;
}

export interface FormSectionProps {
    formId: string;
    onSubmit: Function;
    section: any;
}

export interface NodeReference{
    name: string;
    uid: string;
}

export class DataSource {
    referenceId: string;
    nodeUid: string;
    sourceUid: string;
    sourceUrl: string;
    type: string;
    label: string;
    description: string;
    insertedAt: string;
    insertedBy: string;

    constructor (sourceUid, type, label, description = null) {
        this.sourceUid = sourceUid
        this.type = type
        this.label = label
        this.description = description
    }
}

export interface CaseNode {
    created: string;
    name: string;
    notes: string;
    type: string;
    status: string;
    statusLabel: string;
    statusDescription: string;
    awaitingInformation: boolean;
    targetDate: string;
    targetDateDescription: string;
    lastRun: string;
    parent: NodeReference;
    owner: NodeReference;
    organisationUid: string;
    numberOpenEnquiries: number;
    numberClosedEnquiries: number;
    numberReportsRequested: number;
    persistentAccessUri: string;
    completionNotificationUri: string;
    tags: Record<string, string>;
}

export class ErrorNode {
    
    typeName?: string;
    title?: string;
    detail?: string;
    status?: number;

    constructor (obj: Object) {
        Object.assign(this, obj)
    }
}

export interface CtaInfo {
    buttonText: string;
    action: any;
    helpText?: string;
    color?: "primary" | "secondary" | "success" | "error" | "info";
}

export interface InputFieldProps {
    id?: string;
    value?: any;
    name?: string;
    label?: ReactElement | string;
    formik?: boolean;
    warning?: boolean | string | string[] | FormikErrors<any> | FormikErrors<any>[];
    helpText?: boolean | string;
    style?: any;
    variant?: "standard" | "outlined" | "filled";
    disabled?: boolean;
    required?: boolean;
    autoFocus?: boolean;
    onChange?: Function;
    placeholder?: string;
    autoComplete?: boolean;
    disableUnderline?: boolean;
    slotProps?: any;
}

export interface SessionInfo {
    instanceName: string;
    hostAddress: string;
    version: string;
    versionDate: string;

    sessionId: number;
    userUid: string;
    username: string;
    userEmail: string;
    lastLogin: string;
    lastEnquiryUid: string;

    userAccount: NodeReference;
    businessDate: string;
    displayDictionary: any;
    assessmentSkipEnabled: boolean;
    accountPreferences: any;
    customLogoUrl: string;
    isSelfAdviser: boolean;
}

export interface NodeReference {
    uid: string;
    name: string;
    typeName?: string;
}

export interface AlertArgs {
    message: string;
    color?: "default" | "error" | "success" | "info" | "warning";
    actions?: AlertAction[];
    persist?: boolean;
    closable?: boolean;
    truncRevealer?: boolean;
    verticalAnchor?: "top" | "bottom";
    horizontalAnchor?: "left" | "right" | "center";
}

export interface AlertControl {
    alert: Function;
    dismiss: Function;
}

export interface AlertAction {
	label: string | React.ReactElement;
	action?: Function;
    disabled?: boolean;
	color?: "primary" | "error" | "inherit" | "secondary" | "success" | "info" | "warning";
	variant?: "contained" | "text" | 'outlined';
}

export interface Workflow {
    uid?: string
    label?: string
    description?: string
    createdAt?: string
    updatedAt?: string
    accountUid?: string
    steps?: WorkflowStep[]
}

export interface WorkflowStep {
    uid?: string
    additionalContentName?: string
    label?: string
    description?: string
    orderInd?: number
    targetDateOffsetDays?: number
    // Don't apply to template step
    targetDate?: string | Date
    completedAt?: string | Date
    awaitingInformation?: boolean
    active?: boolean
    additionalContentData?: string
}

export interface WorkflowSummary {
    CaseUid: string;
    Workflow: Workflow;
    ActiveStep: WorkflowStep;
    CaseTargetDate: Date;
}

export interface CaseSummary {
    account: NodeReference;
    case: NodeReference;
    createdAt: Date;
    lastUpdated: Date;
    deletedAt: Date;
    subjectNames: string[];
    users: string[]
    questionnaires: NodeReference[];
    enquiries: NodeReference[];
    automations: NodeReference[];
    tags: any;
    finalTargetDate: Date;
}